import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { scrollToSection } from "@/lib/scroll";
import { CASES } from "@/lib/caseStudies";

export const Work = () => {
  const navigate = useNavigate();
  return (
    <section id="work" data-testid="work-section" className="relative bg-[#060B18] py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mb-16 lg:mb-20 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6"
        >
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400/90 mb-4">Selected Work</p>
            <h2 className="font-serif font-bold tracking-tight leading-[1.05] text-3xl sm:text-5xl lg:text-6xl text-amber-50">
              Proof, not
              <span className="italic text-amber-300"> promises.</span>
            </h2>
          </div>
          <button
            data-testid="work-cta"
            onClick={() => scrollToSection("#contact")}
            className="self-start lg:self-auto font-mono text-xs uppercase tracking-[0.2em] text-amber-300 border-b border-amber-400/40 pb-1 hover:border-amber-300 transition-colors"
          >
            Become a case study
          </button>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {CASES.map((c, i) => (
            <motion.figure
              key={c.client}
              data-testid={c.testid}
              onClick={() => navigate(`/work/${c.slug}`)}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: (i % 2) * 0.12, ease: [0.22, 1, 0.36, 1] }}
              className={`group relative overflow-hidden bg-[#0A1128] border border-amber-500/15 cursor-pointer hover:border-amber-400/40 transition-colors duration-500 ${
                i % 2 === 1 ? "md:mt-14" : ""
              }`}
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={c.img}
                  alt={`${c.client} — ${c.category}`}
                  loading="lazy"
                  className="h-full w-full object-cover grayscale-[35%] transition-all duration-700 group-hover:grayscale-0 group-hover:scale-105"
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#060B18] via-[#060B18]/20 to-transparent" />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-[radial-gradient(circle_at_50%_30%,rgba(212,175,55,0.22),transparent_65%)]" />
                <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] text-amber-200/90 bg-[#060B18]/70 backdrop-blur px-3 py-1.5 border border-amber-400/25">
                  {c.category}
                </span>
                <span className="absolute top-4 right-4 h-3 w-3 border-t border-r border-amber-400/60" aria-hidden="true" />
                <span className="absolute bottom-4 left-4 h-3 w-3 border-b border-l border-amber-400/60" aria-hidden="true" />
              </div>
              <figcaption className="flex items-end justify-between gap-4 p-6 lg:p-8">
                <div>
                  <h3 className="font-serif text-2xl font-semibold text-amber-50">{c.client}</h3>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">{c.category}</p>
                  <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-amber-300/70 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    Read the story →
                  </p>
                </div>
                <p className="font-serif text-xl italic text-amber-300 whitespace-nowrap">{c.metric}</p>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
};
